// src/rms/agreements/tokens.js
// ============================================================================
// The placeholders a contract may carry
// ============================================================================
// The WHITELIST and nothing else. Resolving a token to a figure lives in
// resolve.js, which imports half the app; this file imports nothing at all, on
// purpose, so that scripts/rms/verify-agreements.cjs can load it under plain
// Node without dragging in import.meta.env and a Supabase client.
//
// ----------------------------------------------------------------------------
// Why a whitelist exists when fillTemplate is already forgiving
// ----------------------------------------------------------------------------
// messageService.fillTemplate deliberately leaves an unknown placeholder
// VISIBLE, because "Your booking starts on " with nothing after it reads as a
// broken sentence while "{{starts_at}}" reads as a template somebody has to
// fix. That is right for an email. On a contract it is not enough: an agreement
// that goes out with {{deposit}} still in it is a document a court would read
// as ambiguous. So the editor checks every token against this list, and the
// send path refuses a body with one still unresolved.
//
// A token is NOT a translation key. These resolve to figures, dates and names
// out of the database; they read the same in all thirteen languages, and adding
// one to a catalogue would be a category error.
//
// ----------------------------------------------------------------------------
// What is deliberately NOT a token
// ----------------------------------------------------------------------------
// Mileage allowance and overage rate. They live in rms_usage_fee_schedules,
// keyed by metric and period, and an asset may reference several through
// rms_assets.usage_fee_schedules -- so "the allowance" is not one value, and
// resolving it to a single string would be a guess dressed as a fact. The
// starter templates state the allowance in prose with a blank the owner fills,
// which is honest about where that number actually comes from.
// ============================================================================

/**
 * Every placeholder a template may use, with the hint shown in the editor's
 * token palette. Ordered as a form, not an alphabet.
 */
export const AGREEMENT_TOKENS = [
  // The business -- Settings > Tax & invoicing fills these.
  { key: "business_name", hint: "Your business name" },
  { key: "business_address", hint: "Your address, from Settings" },
  { key: "business_phone", hint: "Your phone number" },
  { key: "business_email", hint: "Your email address" },

  // The renter
  { key: "renter_name", hint: "The renter's full name" },
  { key: "renter_address", hint: "The renter's address, as one line" },
  { key: "renter_email", hint: "The renter's email address" },
  { key: "renter_phone", hint: "The renter's phone number" },
  { key: "licence_number", hint: "Driving licence or ID number on file" },

  // The thing being let
  { key: "asset", hint: "Unit number and name, e.g. CAR-014 - Toyota Corolla" },
  { key: "asset_code", hint: "Unit number alone, e.g. CAR-014" },

  // The booking
  { key: "reservation_number", hint: "e.g. RES-0042" },
  { key: "starts_at", hint: "Pickup date and time" },
  { key: "ends_at", hint: "Return date and time" },
  { key: "pickup_location", hint: "Where the unit is collected" },

  // Money
  { key: "total", hint: "Total charge, formatted in your currency" },
  { key: "deposit", hint: "Security deposit, formatted" },
  { key: "daily_rate", hint: "The unit's base rate, formatted" },

  // The document itself
  { key: "governing_law", hint: "Your country, from Settings. Most operators replace this with their province or state." },
  { key: "today", hint: "The date the document is prepared" },
];

/** Just the names, for the checks below and for the verifier. */
export const TOKEN_KEYS = AGREEMENT_TOKENS.map((x) => x.key);

const PLACEHOLDER = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

const scan = (text, keep) => {
  const found = [];
  PLACEHOLDER.lastIndex = 0;
  let m;
  while ((m = PLACEHOLDER.exec(String(text || "")))) {
    const key = m[1];
    if (keep(key.toLowerCase()) && !found.includes(key)) found.push(key);
  }
  return found;
};

/**
 * Placeholders in a body that this app cannot fill.
 *
 * Called two ways, and the difference is deliberate: the editor calls it to
 * WARN, because an owner mid-sentence should not be blocked; the send path
 * calls it to REFUSE, because a contract with a hole in it should never reach
 * a customer.
 */
export const unknownTokens = (body) => scan(body, (k) => !TOKEN_KEYS.includes(k));

/** Placeholders still sitting in a body AFTER substitution. */
export const unresolvedTokens = (rendered) => scan(rendered, () => true);

export default { AGREEMENT_TOKENS, TOKEN_KEYS, unknownTokens, unresolvedTokens };
